import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useUnreadSupportTicketCount } from '../../services/notificationService';
import { 
  LayoutDashboard, 
  Users, 
  User,
  UserCheck, 
  FileText, 
  Settings,
  Shield,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  BarChart3,
  Calendar,
  CalendarDays,
  CreditCard,
  MessageSquare,
  Mail,
  Bell,
  Lock,
  Activity,
  AlertTriangle,
  TrendingUp,
  DollarSign,
  Package,
  Receipt,
  Newspaper,
  Globe,
  MapPin,
  FileCheck,
  Megaphone,
  LogOut,
  HelpCircle,
  Target,
  Award,
  Trophy,
  Banknote,
  BookOpen,
  RefreshCw,
  Star,
} from 'lucide-react';

function navPathMatches(href, pathname) {
  if (!href || !pathname) return false;
  if (href === '/admin') {
    return pathname === '/admin' || pathname === '/admin/';
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

const AdminSidebar = () => {
  const location = useLocation();
  const { 
    isAdminSidebarCollapsed, 
    toggleAdminSidebar, 
    isAdminMobileSidebarOpen, 
    isAdminMobileSidebarCollapsed,
    toggleAdminMobileSidebar,
    closeAdminMobileSidebar 
  } = useSidebar();
  const { user, logout } = useAuth();

  const { data: unreadSupportTicketsData } = useUnreadSupportTicketCount();
  const unreadSupportTicketsCount = unreadSupportTicketsData ?? 0;
  
  const [openDropdowns, setOpenDropdowns] = useState({});
  
  const toggleDropdown = (dropdownName) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [dropdownName]: !prev[dropdownName]
    }));
  };

  const handleLogout = async () => {
    await logout();
  };
  
  const navigationGroups = [
    {
      type: 'section',
      label: 'Overview',
      items: [{ type: 'link', name: 'Dashboard', href: '/admin', icon: LayoutDashboard }],
    },
    {
      type: 'section',
      label: 'People',
      items: [
        { type: 'link', name: 'Clients', href: '/admin/clients', icon: User },
        { type: 'link', name: 'Vendors', href: '/admin/vendors', icon: UserCheck },
      ],
    },
    {
      type: 'section',
      label: 'Operations',
      items: [
        {
          type: 'dropdown',
          name: 'Marketplace',
          icon: Calendar,
          items: [
            { name: 'Bookings', href: '/admin/bookings', icon: Calendar },
            { name: 'Calendar', href: '/admin/bookings/calendar', icon: CalendarDays },
            { name: 'Vendor Services', href: '/admin/services', icon: Package },
            { name: 'Service Categories', href: '/admin/service-categories', icon: Target },
            { name: 'Countries', href: '/admin/countries', icon: Globe },
            { name: 'Markets', href: '/admin/markets', icon: MapPin },
          ],
        },
      ],
    },
    {
      type: 'section',
      label: 'Finance',
      items: [
        {
          type: 'dropdown',
          name: 'Finance',
          icon: Banknote,
          items: [
            { name: 'Accounting', href: '/admin/accounting', icon: BookOpen },
            { name: 'Payments', href: '/admin/payments', icon: CreditCard },
            { name: 'Refunds', href: '/admin/refunds', icon: RefreshCw },
            { name: 'Subscriptions', href: '/admin/subscriptions', icon: Package },
            { name: 'Subscription Payments', href: '/admin/subscription-payments', icon: Receipt },
            { name: 'Vendor Payouts', href: '/admin/vendor-payouts', icon: Banknote },
            { name: 'Tax Reports', href: '/admin/marketplace-tax-reports', icon: FileText },
          ],
        },
      ],
    },
    {
      type: 'section',
      label: 'Insights',
      items: [
        {
          type: 'dropdown',
          name: 'Insights',
          icon: BarChart3,
          items: [
            { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
            { name: 'Advanced Analytics', href: '/admin/advanced-analytics', icon: TrendingUp },
            { name: 'Reports', href: '/admin/reports', icon: FileCheck },
            { name: 'Services Report', href: '/admin/reports/services', icon: Package },
            { name: 'Revenue', href: '/admin/revenue', icon: DollarSign },
            { name: 'Performance', href: '/admin/performance', icon: Activity },
            { name: 'Failed Operations', href: '/admin/failed-operations', icon: AlertTriangle },
          ],
        },
      ],
    },
    {
      type: 'section',
      label: 'Grow',
      items: [
        {
          type: 'dropdown',
          name: 'Content',
          icon: Newspaper,
          items: [
            { name: 'Content', href: '/admin/content', icon: FileText },
            { name: 'Blog Posts', href: '/admin/blog-posts', icon: BookOpen },
            { name: 'Press Releases', href: '/admin/press-releases', icon: Newspaper },
            { name: 'Job Postings', href: '/admin/job-postings', icon: Users },
            { name: 'Vendor Spotlight', href: '/admin/vendor-spotlight', icon: Award },
            { name: 'Success Stories', href: '/admin/vendor-success-stories', icon: Trophy },
          ],
        },
        {
          type: 'dropdown',
          name: 'Engage',
          icon: MessageSquare,
          items: [
            { name: 'Support Tickets', href: '/admin/support-tickets', icon: HelpCircle },
            { name: 'Reviews', href: '/admin/reviews', icon: Star },
            { name: 'Vendor Support Preview', href: '/admin/support', icon: HelpCircle },
            { name: 'Messages', href: '/admin/messages', icon: MessageSquare },
            { name: 'Contact Inbox', href: '/admin/contact-messages', icon: Mail },
            { name: 'Notifications', href: '/admin/notifications', icon: Bell },
            { name: 'Announcements', href: '/admin/announcements', icon: Megaphone },
            { name: 'System Messages', href: '/admin/system-messages', icon: MessageSquare },
            { name: 'FAQ', href: '/admin/faqs', icon: BookOpen },
          ],
        },
      ],
    },
    {
      type: 'section',
      label: 'Platform',
      items: [
        {
          type: 'dropdown',
          name: 'Platform',
          icon: Settings,
          items: [
            { name: 'Platform handbook', href: '/admin/handbook', icon: BookOpen },
            { name: 'Settings', href: '/admin/settings', icon: Settings },
            { name: 'Email Templates', href: '/admin/email-templates', icon: Mail },
            { name: 'Security', href: '/admin/security', icon: Shield },
            { name: 'System Logs', href: '/admin/system-logs', icon: Activity },
            { name: 'Audit Trail', href: '/admin/audit-trail', icon: FileCheck },
            { name: 'Compliance', href: '/admin/compliance', icon: Shield },
            { name: 'Compliance Audit', href: '/admin/compliance-audit', icon: FileCheck },
            { name: 'Data Protection', href: '/admin/data-protection', icon: Lock },
            { name: 'Terms of Service', href: '/admin/terms-of-service', icon: FileText },
            { name: 'Privacy Policy', href: '/admin/privacy-policy', icon: Lock },
            { name: 'Terms & Privacy', href: '/admin/terms-privacy', icon: FileText },
          ],
        },
      ],
    },
  ];

  useEffect(() => {
    const pathname = location.pathname;
    setOpenDropdowns((prev) => {
      const next = { ...prev };
      let changed = false;
      navigationGroups.forEach((section) => {
        (section.items || []).forEach((group) => {
          if (group.type !== 'dropdown') return;
          const hasActiveChild = group.items.some((item) =>
            navPathMatches(item.href, pathname)
          );
          if (hasActiveChild && !next[group.name]) {
            next[group.name] = true;
            changed = true;
          }
        });
      });
      return changed ? next : prev;
    });
  }, [location.pathname]);

  const isCollapsed = isAdminSidebarCollapsed || isAdminMobileSidebarCollapsed;

  const linkClass = ({ isActive }) =>
    `group flex items-center px-2.5 py-2 text-sm font-medium !rounded-xl transition-colors ${
      isActive
        ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900 shadow-sm'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
    }`;

  const childLinkClass = ({ isActive }) =>
    `group flex items-center justify-between px-2.5 py-2 text-sm font-medium !rounded-xl transition-colors ${
      isActive
        ? 'bg-primary-50 text-primary-900 dark:bg-primary-950/40 dark:text-primary-200'
        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800/80 dark:hover:text-gray-100'
    }`;

  return (
    <>
      {isAdminMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[10990] lg:hidden"
          onClick={closeAdminMobileSidebar}
        />
      )}

      <div
        className={`lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:pt-16 lg:z-40 transition-all duration-300 ${
          isAdminSidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
        } ${
          isAdminMobileSidebarOpen
            ? `fixed inset-y-0 left-0 z-[10995] pt-16 flex flex-col ${
                isAdminMobileSidebarCollapsed ? 'w-16' : 'w-64'
              }`
            : 'hidden'
        }`}
      >
        <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transition-colors">
          <div className="px-3 py-5 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3 px-1">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-sm font-semibold">
                {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
              </div>
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {user?.name || 'Admin'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user?.email || 'admin@example.com'}
                  </p>
                  <span className="mt-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    Admin
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={() => {
                if (window.innerWidth < 1024) {
                  toggleAdminMobileSidebar();
                } else {
                  toggleAdminSidebar();
                }
              }}
              className="w-full flex items-center justify-center p-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 !rounded-xl transition-colors"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-5 overflow-y-auto">
            {navigationGroups.map((section) => (
              <div key={section.label} className="space-y-1">
                {!isCollapsed && (
                  <p className="px-2.5 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
                    {section.label}
                  </p>
                )}
                {section.items.map((group) => {
                  if (group.type === 'link') {
                    return (
                      <NavLink
                        key={group.name}
                        to={group.href}
                        end={group.href === '/admin'}
                        className={linkClass}
                        title={isCollapsed ? group.name : undefined}
                      >
                        <group.icon className={`h-5 w-5 ${isCollapsed ? '' : 'mr-3'} shrink-0`} />
                        {!isCollapsed && group.name}
                      </NavLink>
                    );
                  }

                  if (group.type === 'dropdown') {
                    const hasActiveChild = group.items.some((item) =>
                      navPathMatches(item.href, location.pathname)
                    );
                    const isDropdownOpen = hasActiveChild || !!openDropdowns[group.name];

                    return (
                      <div key={group.name} className="space-y-1">
                        <button
                          type="button"
                          onClick={() => {
                            if (hasActiveChild) return;
                            toggleDropdown(group.name);
                          }}
                          className={`group w-full flex items-center px-2.5 py-2 text-sm font-medium !rounded-xl transition-colors ${
                            isDropdownOpen || hasActiveChild
                              ? 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                          }`}
                          title={isCollapsed ? group.name : undefined}
                          aria-expanded={isDropdownOpen}
                        >
                          <group.icon className={`h-5 w-5 ${isCollapsed ? '' : 'mr-3'} shrink-0`} />
                          {!isCollapsed && (
                            <>
                              <span className="flex-1 text-left">{group.name}</span>
                              <ChevronDown
                                className={`h-4 w-4 transition-transform ${
                                  isDropdownOpen ? 'rotate-180' : ''
                                }`}
                              />
                            </>
                          )}
                        </button>

                        {!isCollapsed && isDropdownOpen && (
                          <div className="ml-3 pl-2 border-l border-gray-200 dark:border-gray-800 space-y-0.5">
                            {group.items.map((item) => {
                              const badgeCount =
                                item.name === 'Support Tickets' ? unreadSupportTicketsCount : 0;
                              return (
                                <NavLink
                                  key={item.name}
                                  to={item.href}
                                  className={childLinkClass}
                                >
                                  <div className="flex items-center min-w-0">
                                    <item.icon className="h-4 w-4 mr-2.5 shrink-0 opacity-70" />
                                    <span className="truncate">{item.name}</span>
                                  </div>
                                  {badgeCount > 0 && (
                                    <span className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold bg-primary-600 text-white rounded-full min-w-[1.25rem] text-center">
                                      {badgeCount > 99 ? '99+' : badgeCount}
                                    </span>
                                  )}
                                </NavLink>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return null;
                })}
              </div>
            ))}
          </nav>

          <div className="px-3 py-4 border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center px-2.5 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40 !rounded-xl transition-colors"
              title={isCollapsed ? 'Logout' : undefined}
            >
              <LogOut className={`h-5 w-5 ${isCollapsed ? '' : 'mr-3'}`} />
              {!isCollapsed && 'Logout'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;
